<template>
    <or-modal
        ref="modal"
        class="recommendation-modal"
        dismiss-on="close-button"
        :title="$t('common.modals.utterance_recommendations.title')"
        @close="closeModal"
    >
        <div
          v-if="isFirstLoading"
          class="first-load-screen"
        >
          <or-progress-circular
            color="primary"
            :size="20"
          ></or-progress-circular>
        </div>
        <div v-else>
          <div>
            <or-alert
              v-show="alertConfig.isShow"
              :type="alertConfig.type"
              :dismissible="false"
            >
              {{alertConfig.message}}
            </or-alert>
            <div class="button-wrap">
              <span
                class="refresh-btn"
                :class="{disabled: isFeatchingRecommendations || isFirstLoading}"
                @click="onRefresh"
              >
                <span class="material-icons">
                  refresh
                </span>
                <span>
                  {{$t('common.modals.utterance_recommendations.table.refresh')}}
                </span>
              </span>
            </div>
          </div>
          <div class="table">
            <div
              v-if="isFeatchingRecommendations"
              class="table__overlaps"
            >
              <or-progress-circular
                class="spinner"
                color="primary"
                :size="20"
              ></or-progress-circular>
            </div>
            <div class="table__header">
              <CustomGroupCheckbox
                class="checkbox"
                :groupState="groupState"
                :disabled="noPhrases"
                @toggle-checkbox-group="toggleCheckboxGroup"
              />
              <span>{{$t('common.modals.utterance_recommendations.table.header')}}</span>
            </div>
            <div
              class="table__body"
              :class="{borderless: !noPhrases }"
            >
              <p
                v-if="noPhrases"
                class='no-phrases-text'
              >
                {{$t('common.modals.utterance_recommendations.noPhrases')}}
              </p>
              <div
                v-else
                class="table-rows"
              >
                <RecommendationItem
                  v-for="(item) in gpt3Recommendations.value"
                  :item="{synonym: item.synonym, id: item.id}"
                  :key="item.id"
                  :selected-phrases="selectedPhrases"
                  :pinned-phrases="pinnedPhrases"
                  :editing-phrase="editingPhrase"
                  :invalid-phrase="invalidPhrase"
                  @toggle="onToggle"
                  @update="onUpdate"
                  @pin="onPinToggle"
                  @empty-phrase-error::show="showEmptyPhraseError"
                  @editing-phrase::set="setEditingPhrase"
                  @invalid-phrase::set="setInvalidPhrase"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="footer">
          <or-button
            type="secondary"
            color="primary"
            class="skip-btn"
            :disabled="isAddingNewPhrases"
            @click="closeModal"
          >
            {{$t('common.modals.utterance_recommendations.skipAll')}}
          </or-button>
          <or-button
            type="primary"
            color="primary"
            :loading="isAddingNewPhrases"
            :disabled="isFirstLoading || isFeatchingRecommendations"
            @click="addRecommendedPhrases"
          >
            {{$t('common.modals.utterance_recommendations.add')}}
          </or-button>
        </div>
    </or-modal>
</template>

<script>
import get from 'lodash/get'
import KnowledgeBus from '@/KnowledgeBus'
import { mapState, mapActions, mapMutations } from 'vuex'
import RecommendationItem from './recommendation_item'
import CustomGroupCheckbox from '@/components/common/custom_group_checkbox'

export default {
  components: {
    RecommendationItem,
    CustomGroupCheckbox
  },

  created () {
    KnowledgeBus.$on('phrases-recommendations::show', this.openModal)
  },

  beforeDestroy () {
    KnowledgeBus.$off('phrases-recommendations::show', this.openModal)
  },

  computed: {
    ...mapState('knowledgebase/utterances', ['gpt3Recommendations']),

    noPhrases () {
      return get(this.gpt3Recommendations, 'value', []).length === 0
    },

    allIds () {
      if (this.gpt3Recommendations.value) {
        return this.gpt3Recommendations.value.map(item => item.id)
      }

      return []
    },

    groupState () {
      if (this.allIds.every(id => this.selectedPhrases.includes(id)) &&
        (this.selectedPhrases.length > 0)) {
        return 'checked'
      }
      if (this.allIds.some(id => this.selectedPhrases.includes(id))) {
        return 'some-checked'
      }

      return 'not-checked'
    }
  },

  data () {
    return {
      isFirstLoading: false,
      isFeatchingRecommendations: false,
      isAddingNewPhrases: false,
      currentUtterance: {},
      selectedPhrases: [], // []ids
      pinnedPhrases: [], // []ids
      alertConfig: {
        isShow: false,
        type: '',
        message: ''
      },
      editingPhrase: null,
      invalidPhrase: null
    }
  },

  methods: {
    ...mapActions('knowledgebase/utterances', ['batchCreatePhrases', 'getGpt3Recommendations']),
    ...mapMutations('knowledgebase/utterances', ['SET_GPT3_RECOMMENDATIONS']),

    closeModal () {
      this.$refs.modal.close()
    },

    async openModal (utterance) {
      this.isAddingNewPhrases = false
      this.setAlertConfig({ isShow: false })
      this.editingPhrase = null
      this.invalidPhrase = null
      this.currentUtterance = utterance
      this.selectedPhrases = []
      this.$refs.modal.open()
      this.isFirstLoading = true
      await this.getGpt3Recommendations({ phrase: utterance.text || utterance })
      this.isFirstLoading = false
    },

    async addRecommendedPhrases () {
      if (this.selectedPhrases.length === 0) {
        const message = this.$t('common.modals.utterance_recommendations.errors.isNothingToAdd')
        this.setAlertConfig({ isShow: true, type: 'warning', message })

        return
      }

      if (this.editingPhrase) {
        const message = this.$t('common.modals.utterance_recommendations.errors.isEditingNotComplete')
        this.setAlertConfig({ isShow: true, type: 'error', message })
        this.setInvalidPhrase(this.editingPhrase)

        return
      }

      this.setAlertConfig({ isShow: false })

      const utterances = this.selectedPhrases.map(phraseId => {
        const item = this.gpt3Recommendations.value.find(item => item.id === phraseId)

        return _.pick({ ...this.currentUtterance, text: item.synonym }, [
          'application',
          'intent',
          'intentType',
          'text'
        ])
      })

      this.isAddingNewPhrases = true
      await this.batchCreatePhrases({ utterances }) // []utterances
      this.isAddingNewPhrases = false

      this.closeModal()
    },

    onToggle (id) {
      this.selectedPhrases = this.selectedPhrases.includes(id)
        ? _.filter(this.selectedPhrases, phraseId => phraseId !== id)
        : [...this.selectedPhrases, id]
    },

    toggleCheckboxGroup () {
      if (_.every(this.allIds, id => _.includes(this.selectedPhrases, id))) {
        this.selectedPhrases = _.filter(this.selectedPhrases, phraseId => !_.includes(this.allIds, phraseId))
      } else {
        this.allIds.forEach(id => {
          if (!this.selectedPhrases.includes(id)) {
            this.selectedPhrases.push(id)
          }
        })
      }
    },

    async onRefresh () {
      if (this.isFeatchingRecommendations) return

      if (this.pinnedPhrases.includes(this.editingPhrase)) {
        const message = this.$t('common.modals.utterance_recommendations.errors.isEditingNotComplete')
        this.setAlertConfig({ isShow: true, type: 'error', message })
        this.setInvalidPhrase(this.editingPhrase)

        return
      }

      this.editingPhrase = null
      this.selectedPhrases = []
      this.isFeatchingRecommendations = true
      await this.getGpt3Recommendations({
        phrase: this.currentUtterance.text,
        pinnedIds: this.pinnedPhrases
      })
      this.isFeatchingRecommendations = false
    },

    onUpdate (value) {
      const index = this.gpt3Recommendations.value.findIndex(item => item.id === value.id)
      const updatedRecommendations = this.gpt3Recommendations.value
      updatedRecommendations.splice(index, 1, { synonym: value.synonym, id: value.id })
      this.SET_GPT3_RECOMMENDATIONS({ value: updatedRecommendations })
    },

    onPinToggle (id) {
      if (this.pinnedPhrases.includes(id)) {
        this.pinnedPhrases = this.pinnedPhrases.filter(pid => pid !== id)
      } else {
        this.pinnedPhrases.push(id)
      }
    },

    setEditingPhrase (phraseId) {
      this.editingPhrase = phraseId
    },

    setAlertConfig ({ isShow, type = '', message = '' }) {
      this.alertConfig = { isShow, type, message }
    },

    showEmptyPhraseError (value) {
      this.setAlertConfig({
        isShow: value,
        type: 'error',
        message: this.$t('common.modals.utterance_recommendations.errors.isEmpty')
      })
    },

    setInvalidPhrase (phraseId) {
      this.invalidPhrase = phraseId
    }
  }
}
</script>
<style lang="scss" scoped>
.recommendation-modal {
  z-index: 999;

  .first-load-screen {
    display: flex;
    justify-content: center;
  }

  .spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1000;
  }

  .button-wrap {
    display: flex;
    align-items: center;
    height: 36px;

    .refresh-btn {
      display: flex;
      align-items: center;
      color: $or-grey-dark ;
      font-size: 12px;
      user-select: none;

      &.disabled {
        color: $or-grey
      }

      &:hover:not(.disabled) {
        cursor: pointer;
        color: darken($or-grey-dark, 10%);
      }

      .material-icons {
        font-size: 16px;
        margin-right: 8px;
      }
    }
  }

  .table {
    position: relative;

    &__overlaps {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      background-color: rgba(255, 255, 255, 0.6);
      z-index: 999;
    }

    &__header {
      display: flex;
      align-items: center;
      font-size: 14px;
      line-height: 16px;
      font-weight: bold;
      background-color: $or-grey-medium;
      border: solid 1px $or-grey-medium;
      border-top-left-radius: 4px;
      border-top-right-radius: 4px;
      height: 36px;
      padding: 10px;

      .checkbox {
        margin-right: 22px;
      }
    }

    &__body {
      display: flex;
      justify-content: center;
      min-height: 36px;
      position: relative;
      border: solid 1px $or-grey-medium;
      border-bottom-left-radius: 4px;
      border-bottom-right-radius: 4px;

      &.borderless {
        border: none;
      }

      .no-phrases-text {
        display: flex;
        align-items: center;
        text-align: center;
        margin: 0;
        font-size: 14px;
        line-height: 21px;
        font-weight: normal;
      }

      .table-rows {
        display: flex;
        flex-direction: column;
        width: 100%;
      }
    }
  }

  .footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 32px;
    margin-bottom: 16px;

    .skip-btn {
      margin-right: 16px;
    }
  }
}
</style>
<style lang="scss">
.recommendation-modal {
  .ui-modal__container {
    width: 700px;
  }
}
</style>
